export type ZipSignedUrlEntry = { url: string; destination: string };

export type ZipEntry = { name: string; input: ReadableStream<Uint8Array> };

export type ZipDownloadCoreState = {
  downloaded: number;
  failedCount: number;
  total: number;
  bytesReceived: number;
};

export type ZipDownloadProgressEvent = {
  downloaded: number;
  failed: number;
  total: number;
  phase: 'fetching' | 'zipping';
  bytesReceived: number;
  totalBytes: number | null;
  percent: number;
};

export const ZIP_PROGRESS_SHARE = 10;
export const PROGRESS_COALESCE_MS = 100;
export const DOWNLOAD_CANCELLED_MESSAGE = 'Download cancelled';

export class ZipDownloadAbortedError extends Error {
  constructor() {
    super(DOWNLOAD_CANCELLED_MESSAGE);
    this.name = 'ZipDownloadAbortedError';
  }
}

export function isZipDownloadAbortedError(error: unknown): boolean {
  return (
    error instanceof ZipDownloadAbortedError ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export function normalizeZipPath(destination: string): string {
  return destination
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .split('/')
    .filter((segment) => segment && segment !== '.' && segment !== '..')
    .join('/');
}

export function computeZipPercent(
  phase: 'fetching' | 'zipping',
  filesCompleted: number,
  totalFiles: number,
  currentFileFraction: number,
): number {
  if (phase === 'zipping') return 100 - ZIP_PROGRESS_SHARE;
  if (totalFiles <= 0) return 0;
  const fetchShare = 100 - ZIP_PROGRESS_SHARE;
  const fraction = (filesCompleted + currentFileFraction) / totalFiles;
  return Math.min(fetchShare, Math.max(0, fraction * fetchShare));
}

export function createCoalescedProgressHandler(
  // eslint-disable-next-line no-unused-vars
  emit: (progress: ZipDownloadProgressEvent) => void,
  intervalMs = PROGRESS_COALESCE_MS,
  // eslint-disable-next-line no-unused-vars
): (progress: ZipDownloadProgressEvent) => void {
  let lastEmitAt = 0;
  let lastFilesCompleted = -1;

  return (progress) => {
    const filesCompleted = progress.downloaded + progress.failed;
    const force = progress.phase === 'zipping' || filesCompleted !== lastFilesCompleted;
    lastFilesCompleted = filesCompleted;
    const now = Date.now();
    if (!force && now - lastEmitAt < intervalMs) return;
    lastEmitAt = now;
    emit(progress);
  };
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError') ||
    error instanceof ZipDownloadAbortedError
  );
}

function parseContentLength(response: Response): number | null {
  const raw = response.headers.get('content-length');
  if (!raw) return null;
  const length = Number(raw);
  return Number.isFinite(length) && length > 0 ? length : null;
}

function createByteCountingTransform(
  state: ZipDownloadCoreState,
  contentLength: number | null,
  abortController: AbortController,
  // eslint-disable-next-line no-unused-vars
  onChunk: (fileFraction: number) => void,
): TransformStream<Uint8Array, Uint8Array> {
  let bytesThisFile = 0;
  return new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      if (abortController.signal.aborted) {
        controller.error(new ZipDownloadAbortedError());
        return;
      }
      bytesThisFile += chunk.byteLength;
      state.bytesReceived += chunk.byteLength;
      const fraction =
        contentLength && contentLength > 0 ? Math.min(1, bytesThisFile / contentLength) : 0;
      onChunk(fraction);
      controller.enqueue(chunk);
    },
  });
}

export async function* createSignedUrlEntryGenerator(
  signedUrls: ZipSignedUrlEntry[],
  state: ZipDownloadCoreState,
  abortController: AbortController,
  // eslint-disable-next-line no-unused-vars
  onProgress?: (progress: ZipDownloadProgressEvent) => void,
): AsyncGenerator<ZipEntry> {
  state.total = signedUrls.length;
  state.downloaded = 0;
  state.failedCount = 0;
  state.bytesReceived = 0;

  const emit = (phase: 'fetching' | 'zipping', currentFileFraction = 0) => {
    const filesCompleted = state.downloaded + state.failedCount;
    onProgress?.({
      downloaded: state.downloaded,
      failed: state.failedCount,
      total: state.total,
      phase,
      bytesReceived: state.bytesReceived,
      totalBytes: null,
      percent: computeZipPercent(phase, filesCompleted, state.total, currentFileFraction),
    });
  };

  emit('fetching', 0);

  if (signedUrls.length === 0) {
    return;
  }

  const fetchAt = (index: number) =>
    fetch(signedUrls[index].url, { signal: abortController.signal });

  let pendingFetch = fetchAt(0);

  for (let i = 0; i < signedUrls.length; i++) {
    if (abortController.signal.aborted) {
      throw new ZipDownloadAbortedError();
    }

    const { destination } = signedUrls[i];

    try {
      const response = await pendingFetch;
      if (i + 1 < signedUrls.length) {
        pendingFetch = fetchAt(i + 1);
      }

      if (!response.ok || !response.body) {
        state.failedCount += 1;
        emit('fetching', 0);
        continue;
      }

      const path = normalizeZipPath(destination);
      if (!path) {
        state.failedCount += 1;
        emit('fetching', 0);
        continue;
      }

      const contentLength = parseContentLength(response);
      const countedBody = response.body.pipeThrough(
        createByteCountingTransform(state, contentLength, abortController, (fraction) =>
          emit('fetching', fraction),
        ),
      );

      yield { name: path, input: countedBody };
      state.downloaded += 1;
      emit('fetching', 0);
    } catch (error) {
      if (isAbortError(error) || abortController.signal.aborted) {
        throw new ZipDownloadAbortedError();
      }
      if (i + 1 < signedUrls.length) {
        pendingFetch = fetchAt(i + 1);
      }
      state.failedCount += 1;
      emit('fetching', 0);
    }
  }

  emit('zipping', 0);
}
